import { InfoKeyValue } from '@/components/info-key-value.js';
import { Button } from '@/components/ui/button.js';
import { LoaderIcon } from '@/components/ui/loader-icon.js';
import { DialogHeader, Dialog, DialogTrigger, DialogContent, DialogTitle } from '@/components/ui/dialog.js';
import { getCoachVendorForAdmin } from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import { maskedValueClassName } from '@/lib/masked-value.js';
import {
  bulkSendTeacherPaymentFailureReasonKind,
  bulkSendTeacherPaymentFailureReasonToLabel,
  CoachPaymentDepositInfoStatus,
  coachPaymentDepositInfoStatusSchema,
  getCoachACHPaymentOrNull,
  getCoachPaymentInternationalOrNull,
} from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { DepositInfoStatus } from './deposit-info-status.js';

type PaymentInstrumentModalProps = {
  paymentInstrumentId: string | null;
  depositInfoStatus: CoachPaymentDepositInfoStatus;
};

export const PaymentInstrumentModal = (props: PaymentInstrumentModalProps) => {
  const { paymentInstrumentId, depositInfoStatus } = props;
  const [isOpen, setIsOpen] = useState(false);

  const {
    data: vendor,
    isError,
    error,
    isFetched,
    isFetching,
  } = useQuery({
    ...getCoachVendorForAdmin(paymentInstrumentId ?? undefined),
    enabled: !!paymentInstrumentId && isOpen,
  });

  if (!paymentInstrumentId) return null;

  const achVendor = getCoachACHPaymentOrNull(vendor);
  const internationalVendor = getCoachPaymentInternationalOrNull(vendor);
  const showDetails = Boolean(achVendor || internationalVendor);
  const settled = isFetched && !isFetching;
  const confirmedMissing =
    depositInfoStatus !== coachPaymentDepositInfoStatusSchema.enum.ready ||
    (isError && airServerErrorFrom(error)?.message === 'record_not_found');
  const showInvalid = settled && !showDetails && confirmedMissing;
  const showUnverified = settled && !showDetails && !confirmedMissing;
  const statusForDisplay =
    confirmedMissing && depositInfoStatus === coachPaymentDepositInfoStatusSchema.enum.ready
      ? coachPaymentDepositInfoStatusSchema.enum.not_ready
      : depositInfoStatus;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger>
        <Button variant="link" className="p-0 h-auto text-blue-600 hover:text-blue-800 no-underline">
          {paymentInstrumentId}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Brex - Bank information</DialogTitle>
          {achVendor && (
            <div className="grid grid-cols-2 pt-4 gap-4">
              <InfoKeyValue label="Account Holder Name" value={achVendor?.company_name} />
              <InfoKeyValue label="Account Type" value={achVendor?.payment_accounts?.details.account_type} />
              <InfoKeyValue
                label="Routing Number"
                labelClassName={maskedValueClassName}
                value={achVendor?.payment_accounts?.details.masked_routing_number}
              />
              <InfoKeyValue
                label="Account Number"
                labelClassName={maskedValueClassName}
                value={achVendor?.payment_accounts?.details.masked_account_number}
              />
              <InfoKeyValue label="Status" value={<DepositInfoStatus depositInfoStatus={depositInfoStatus} />} />
            </div>
          )}
          {internationalVendor && (
            <div className="grid grid-cols-2 pt-4 gap-4">
              <InfoKeyValue label="Account Holder Name" value={internationalVendor.company_name} />
              <InfoKeyValue
                label="Swift / BIC code"
                labelClassName={maskedValueClassName}
                value={internationalVendor.payment_accounts?.details.masked_swift_code}
              />
              <InfoKeyValue
                label="IBAN / Account number"
                labelClassName={maskedValueClassName}
                value={internationalVendor.payment_accounts?.details.masked_iban}
              />
              <InfoKeyValue label="Country" value={internationalVendor.payment_accounts?.details.address.country} />
              <InfoKeyValue label="City" value={internationalVendor.payment_accounts?.details.address.city} />
              <InfoKeyValue
                label="Address"
                value={
                  (internationalVendor?.payment_accounts.details.address.line1 ?? '') +
                  ' ' +
                  (internationalVendor?.payment_accounts.details.address.line2 ?? '')
                }
              />
              <InfoKeyValue label="Status" value={<DepositInfoStatus depositInfoStatus={depositInfoStatus} />} />
              <InfoKeyValue
                label="Zip code"
                value={internationalVendor.payment_accounts?.details.address.postal_code}
              />
            </div>
          )}
          {isFetching && !showDetails && (
            <div role="status" className="flex items-center gap-2 pt-4">
              <LoaderIcon className="h-4 w-4" />
              <span>Checking direct deposit status.</span>
            </div>
          )}
          {showInvalid && (
            <div className="pt-4 space-y-2" data-testid="teacher-pay-modal-deposit-invalid">
              <DepositInfoStatus depositInfoStatus={statusForDisplay} />
              <p role="status">
                {
                  bulkSendTeacherPaymentFailureReasonToLabel[
                    bulkSendTeacherPaymentFailureReasonKind.enum.stale_deposit_info
                  ]
                }
              </p>
            </div>
          )}
          {showUnverified && (
            <p role="status" className="pt-4" data-testid="teacher-pay-modal-deposit-unverified">
              {
                bulkSendTeacherPaymentFailureReasonToLabel[
                  bulkSendTeacherPaymentFailureReasonKind.enum.brex_unavailable
                ]
              }
            </p>
          )}
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
